import { useCallback, useEffect, useState } from "react";
import { apiError } from "@/lib/api";
import { EmptyState } from "@/components/common";
import { ListFilter, useListFilter } from "@/components/common/ListFilter";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, Plus } from "lucide-react";
import { supplierService } from "@/services/supplierService";
import { SupplierCard, SupplierDialog } from "./SupplierParts";

const emptySup = { name: "", contact: "", address: "", first_name: "", last_name: "", email: "",
  phone_mobile_cc: "+49", phone_mobile_no: "", phone_landline_cc: "+49", phone_landline_no: "" };

export default function SuppliersTab({ canEdit }) {
  const { t } = useI18n();
  const [suppliers, setSuppliers] = useState([]);
  const [supOpen, setSupOpen] = useState(false);
  const [sup, setSup] = useState(emptySup);
  const [supEditId, setSupEditId] = useState(null);

  const load = useCallback(() => {
    supplierService.list().then((d) => setSuppliers([...d].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSupplier = async () => {
    try {
      if (supEditId) { await supplierService.update(supEditId, sup); toast.success(t("sup.updated")); }
      else { await supplierService.create(sup); toast.success(t("sup.created")); }
      setSupOpen(false); setSupEditId(null); setSup(emptySup); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEditSupplier = (s) => { setSupEditId(s.id); setSup({ ...emptySup, ...s, phone_mobile_cc: s.phone_mobile_cc || "+49", phone_landline_cc: s.phone_landline_cc || "+49" }); setSupOpen(true); };
  const delSupplier = async (s) => {
    if (!window.confirm(t("sup.confirmDelete", { name: s.name }))) return;
    try { await supplierService.remove(s.id); toast.success(t("prod.deleted")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filter = useListFilter(suppliers, { fields: ["name", "contact", "email", "address"], storageKey: "b2bSuppliers" });

  return (
    <>
      <ListFilter filter={filter} testId="suppliers" placeholder={t("filter.searchName")}
        exportName="lieferanten" exportColumns={[
          { label: t("common.name"), value: (s) => s.name },
          { label: t("ord.contactPerson"), value: (s) => s.contact || `${s.first_name || ""} ${s.last_name || ""}`.trim() },
          { label: "E-Mail", value: (s) => s.email || "" },
          { label: t("sup.phoneMobile", "Mobil"), value: (s) => `${s.phone_mobile_cc || ""}${s.phone_mobile_no || ""}` },
          { label: t("ord.shippingAddress"), value: (s) => (s.address || "").replace(/\s*\n\s*/g, ", ") },
        ]} />
      {canEdit && <div className="flex justify-end mb-3"><Button data-testid="new-supplier-btn" onClick={() => { setSupEditId(null); setSup(emptySup); setSupOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> {t("sup.new")}</Button></div>}
      {suppliers.length === 0 ? <EmptyState testId="suppliers-empty" icon={Truck} title={t("sup.emptyTitle")} hint={t("sup.emptyHint")} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="suppliers-list">
          {filter.filtered.map((s) => <SupplierCard key={s.id} s={s} canEdit={canEdit} onEdit={openEditSupplier} onDelete={delSupplier} />)}
        </div>
      )}
      <SupplierDialog open={supOpen} onOpenChange={(o) => { setSupOpen(o); if (!o) setSupEditId(null); }} sup={sup} setSup={setSup} supEditId={supEditId} onSave={saveSupplier} />
    </>
  );
}
